import * as R from 'ramda';
// constants
import * as C from '../../constants';
// utilities
import * as U from '../../utilities';
//////////////////////////////////////////////////

const buttons = handleRemoveAction => ([
  {
    width: 'auto',
    type: 'button',
    action: handleRemoveAction,
    btnText: U.getTranslation('actions.remove', 'Remove'),
  },
]);

const getAvailableForReportFilter = (searchCriteria, availableFields) => availableFields?.filter(af => (
  R.not(searchCriteria?.find(f => R.equals(f?.propertyName, af?.name)))
));

const getAvailableForReports = (props, reportFieldList) => {
  const { availableFields } = props;

  return availableFields?.filter(af => (
    R.not(reportFieldList?.find(f => R.equals(f?.name, af?.name)))
  ));
};

const getAvailableForReportSort = (props, reportFieldList) => {
  const availableForSort = R.compose(
    R.reject(({ disableSort, disableFilter }) => {
      if (U.isBoolean(disableSort)) return disableSort;

      return disableFilter;
    }),
    R.pathOr([], ['availableFields']),
  )(props);

  return availableForSort.filter(af => (
    R.not(reportFieldList.find(f => R.equals(f.name, af.name)))
  ));
};

const mapFieldsWithSequenceFromIndex = fields => U.mapIndexed(
  (field, index) => R.assoc('sequence', R.inc(index), field),
  fields,
);

const createSelectMultipleValue = item => {
  if (U.isNilOrEmpty(R.path(['selectMultipleValue'], item))) return '';

  return R.join(',', R.map(R.prop(C.FIELD_VALUE), R.path(['selectMultipleValue'], item)));
};

const checkDataTypeInSearchCriteriaObject = item => R.compose(
  R.set(R.lensProp('dataType'), 'string'),
  R.assoc('stringValue', createSelectMultipleValue(item)),
  R.omit(['selectMultipleValue']),
)(item);

const transformSearchCriteriaBeforeReportPost = data => R.compose(
  R.map(item => {
    const { dataType } = item;

    const booleanValue = R.path(['booleanValue'], item);

    if (R.equals(dataType, 'selectMultiple')) return checkDataTypeInSearchCriteriaObject(item);

    const stringDataTypes = ['string:array'];

    if (R.includes(dataType, stringDataTypes)) return R.assoc('dataType', 'string', item);

    if (R.equals(dataType, 'boolean')) {
      if (R.equals(booleanValue, 'Yes')) {
        return R.assoc('booleanValue', true, item);
      }

      if (R.equals(booleanValue, 'No')) {
        return R.assoc('booleanValue', false, item);
      }
    }

    if (R.equals(dataType, 'string')) {
      return R.assoc('stringValue', R.toUpper(U.getPropFromObject('stringValue', item)), item);
    }

    return item;
  }),
)(data);

const buildReport = props => {
  const {
    isCreate,
    isUpdate,
    usedReport,
    showIssues,
    reportSortList,
    reportFieldList,
    reportFilterGroups,
    disableSetReportFields,
    avoidRelatedValueDuplicates,
  } = props;

  let searchCriteria = R.compose(
    R.map(filter => {
      const {
        uuid,
        name,
        groupId,
      } = filter;

      const groupIdToUse = groupId;
      const idToUse = U.ifElse(isCreate, null, uuid);

      return {
        ...filter,
        uuid: idToUse,
        [C.FIELD_NAME]: name,
        groupId: groupIdToUse,
      };
    }),
    R.reject(field => U.isNilOrEmpty(field.propertyName)),
    R.filter(field => U.notEquals(field.dataType, '')),
    R.reduce(R.concat, []),
    R.values,
  )(reportFilterGroups);

  if (R.or(isCreate, isUpdate)) searchCriteria = transformSearchCriteriaBeforeReportPost(searchCriteria);

  const orderFields = R.compose(
    U.mapIndexed((filter, i) => {
      const { uuid, name } = filter;

      const idToUse = U.ifElse(isCreate, null, uuid);

      return R.mergeRight(filter, {
        sequence: R.inc(i),
        [C.FIELD_NAME]: name,
        [C.FIELD_UUID]: idToUse,
      });
    }),
    R.filter(({ name }) => U.isNotNilAndNotEmpty(name)),
  )(R.or(reportSortList, []));

  let fieldsToUse = [];
  const summaryParams = {};

  if (R.not(disableSetReportFields)) {
    fieldsToUse = R.compose(
      R.map(field => {
        const { uuid, name } = field;

        return R.mergeRight(field, {
          [C.FIELD_NAME]: name,
          [C.FIELD_UUID]: U.ifElse(isCreate, null, uuid),
        });
      }),
      R.filter(field => U.notEquals(field.name, '')),
    )(reportFieldList);
  }

  fieldsToUse = R.compose(
    R.filter(({ name }) => U.isNotNilAndNotEmpty(name)),
    mapFieldsWithSequenceFromIndex,
  )(fieldsToUse);

  return {
    ...usedReport,
    ...summaryParams,
    showIssues,
    orderFields,
    searchCriteria,
    fields: fieldsToUse,
    avoidRelatedValueDuplicates,
  };
};

const validateOperation = report => {
  let operationAbsent = false;

  report.searchCriteria.forEach(criteria => {
    const condition = U.ifElse(
      R.equals(criteria.dataType, C.FIELD_DATE),
      R.and(U.isNilOrEmpty(criteria.timeUnit), U.isNilOrEmpty(criteria.operation)),
      U.isNilOrEmpty(criteria.operation),
    );

    if (condition) operationAbsent = true;
  });

  return operationAbsent;
};

const validateRange = report => {
  let rangeAbsent = false;

  report.searchCriteria.forEach(criteria => {
    const { dataType, operation } = criteria;

    let condition = false;

    if (R.equals(operation, 'range')) {
      const rangeValues = R.prop(`${dataType}Range`, criteria);
      condition = U.ifElse(
        R.all(U.isNilOrEmpty, R.values(rangeValues)),
        false,
        R.any(U.isNilOrEmpty, R.values(rangeValues)),
      );
    }

    if (condition) rangeAbsent = true;
  });

  return rangeAbsent;
};

const isReportValid = report => {
  const rangeAbsent = validateRange(report);
  const operationAbsent = validateOperation(report);

  if (rangeAbsent) {
    U.showToastrMessageSimple('info', U.getTranslation(
      'errors.rangeFieldsShouldBothBeEmpty',
      'Range fields should both be empty or both filled in',
    ));

    return false;
  }
  if (operationAbsent) {
    U.showToastrMessageSimple('info', U.getTranslation(
      'errors.selectOperationsFoAllSelectedFilterFields',
      'Please, select operations for all selected filter fields',
    ));

    return false;
  }

  return true;
};

const isNotReportValid = report => R.not(isReportValid(report));

const getAvailableFilterFields = props => {
  const { availableFields } = props;

  return R.filter(
    ({ disableFilter }) => R.not(disableFilter),
    R.or(availableFields, []),
  );
};

const getIndexesFromDnDResult = result => {
  const sourceIndex = R.path(['source', 'index'], result);
  const destinationIndex = R.path(['destination', 'index'], result);
  const destinationIndexChecked = U.ifElse(
    R.equals(destinationIndex, -1),
    0,
    destinationIndex,
  );
  return {
    sourceIndex,
    destinationIndexChecked,
  };
};

const reorderFieldsOnDrugEnd = (sortedFields, result) => {
  const { sourceIndex, destinationIndexChecked } = getIndexesFromDnDResult(result);

  const fields = R.clone(sortedFields);
  const [removedEvent] = fields.splice(sourceIndex, 1);

  fields.splice(destinationIndexChecked, 0, removedEvent);

  return U.mapIndexed((stop, index) => R.assoc('sequence', R.inc(index), stop), fields);
};

const setOptionLabelFromNameOrValue = options => R.map(
  option => {
    const { name, label, value } = option;

    if (R.and(U.isNotNilAndNotEmpty(label), U.isNotNilAndNotEmpty(value))) return option;

    if (R.and(U.isNilOrEmpty(label), U.isNotNilAndNotEmpty(value))) return R.assoc(C.FIELD_LABEL, R.or(name, value), option);

    if (R.and(U.isNilOrEmpty(value), U.isNotNilAndNotEmpty(label))) return R.assoc(C.FIELD_VALUE, R.or(label, name), option);

    return R.mergeLeft({[C.FIELD_LABEL]: name, [C.FIELD_VALUE]: name}, option);
  },
  R.or(options, []),
);

export {
  buttons,
  buildReport,
  validateRange,
  isReportValid,
  isNotReportValid,
  validateOperation,
  reorderFieldsOnDrugEnd,
  getAvailableForReports,
  getAvailableFilterFields,
  createSelectMultipleValue,
  getAvailableForReportSort,
  getAvailableForReportFilter,
  setOptionLabelFromNameOrValue,
  mapFieldsWithSequenceFromIndex,
  transformSearchCriteriaBeforeReportPost,
};
